import { useState, type FormEvent } from 'react';

import { describeError } from '../../ui/problemDetails.ts';
import { useCreateStore } from './useAisles.ts';

/**
 * Adds another shop to the household — "Aldi", "the corner shop", "the big Sainsbury's".
 *
 * One field and one button, matching the new-list form beside it: adding a shop is a
 * once-in-a-while act and does not need explaining every time the panel opens.
 *
 * A new shop starts with the aisles in their usual order rather than a copy of whichever
 * one was open. It is a different building — seeding it from the last one would make
 * "already arranged" and "not arranged yet" look the same on the screen.
 */
export function NewStoreForm({ groupId }: { groupId: string }) {
  const [name, setName] = useState('');
  const create = useCreateStore(groupId);

  const trimmed = name.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      create.mutate(trimmed, { onSuccess: () => setName('') });
    }
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">New shop</h3>

      <form className="settings-row" onSubmit={handleSubmit}>
        <input
          className="settings-row__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Aldi"
          aria-label="New shop name"
          maxLength={80}
        />
        <button
          className="button button--primary button--small"
          type="submit"
          disabled={create.isPending || trimmed.length === 0}
        >
          Add
        </button>
      </form>

      {create.isError && (
        <p className="form-error" role="alert">
          {describeError(create.error)}
        </p>
      )}
    </section>
  );
}
